<template>
  <div
    v-if="hasPopularity || hasEngagementData || isRecommended"
    class="c-popularity-and-engagement-icons flex flex-wrap"
  >
    <NTooltip
      v-if="hasPopularity"
      class="!bg-base !color-text"
      :style="{
        '--n-color': themeVars.base,
      }"
      :trigger="isTouchScreen ? 'click' : 'hover'"
    >
      <template #trigger>
        <div
          class="flex items-center justify-center rounded-full"
          :class="`${variation === 'minimal' ? 'w-[24px] h-[24px]' : ''} ${getPopularityClass()}`"
          @click.stop
        >
          <CTag
            v-if="variation !== 'minimal'"
            :class="`flex gap-1 ${getPopularityClass()}`"
            size="small"
            :style="{
              '--n-text-color':
                popularity?.category === SectionPopularityCategory.THE_MOST_POPULAR
                  ? themeVars.base
                  : popularity?.category === SectionPopularityCategory.POPULAR
                    ? themeVars.info
                    : themeVars.text,
              '--n-padding': '4px 8px',
              border: 'none',
            }"
          >
            <template #icon>
              <CIcon class="w-full ml-1" :icon="getPopularityIcon()" size="12px" />
            </template>
            <div v-if="variation === 'extended'">
              <span>{{ popularity?.value }}% </span>
              <span>{{ t('canvasUI.CPopularityAndEngagementIcons.popularityScore') }}</span>
            </div>
            <div v-else-if="variation === 'compact'">
              <span>{{ popularity?.value }}% </span>
            </div>
          </CTag>
          <CIcon v-else :icon="getPopularityIcon()" size="12px" />
        </div>
      </template>

      <span v-if="popularity?.value">
        {{ popularity?.value }}% {{ t('canvasUI.CPopularityAndEngagementIcons.popularityScore') }},
      </span>

      <span v-if="views">{{ t('canvasUI.CPopularityAndEngagementIcons.views') }}: {{ views }}</span>
    </NTooltip>
    <NTooltip
      v-if="hasEngagementData"
      class="popularity-tooltip !bg-base !color-text"
      :style="{
        '--n-color': themeVars.base,
      }"
      :trigger="isTouchScreen ? 'click' : 'hover'"
    >
      <template #trigger>
        <div
          class="flex items-center justify-center rounded-full"
          :class="`${variation === 'minimal' ? 'w-[24px] h-[24px]' : ''} ${getEngagementClass()}`"
          @click.stop
        >
          <CTag
            v-if="variation !== 'minimal'"
            :class="`flex gap-1 ${getEngagementClass()}`"
            size="small"
            :style="{
              '--n-text-color':
                engagement?.direction === SectionTrendlineDirection.ASC ? themeVars.success : themeVars.text,
              '--n-padding': '4px 8px',
              border: 'none',
            }"
          >
            <template #icon>
              <CIcon class="ml-1" icon="wave-pulse" size="12px" />
            </template>

            <div v-if="variation === 'extended'">
              <span v-if="engagement?.direction === SectionTrendlineDirection.ASC">{{
                t('canvasUI.CPopularityAndEngagementIcons.highEngagement')
              }}</span>
              <span v-else>{{ t('canvasUI.CPopularityAndEngagementIcons.lowEngagement') }}</span>
            </div>
            <div v-else-if="variation === 'compact'">
              <span v-if="engagement?.direction === SectionTrendlineDirection.ASC">{{
                t('canvasUI.CPopularityAndEngagementIcons.high')
              }}</span>
              <span v-else>{{ t('canvasUI.CPopularityAndEngagementIcons.low') }}</span>
            </div>
          </CTag>
          <CIcon v-else icon="wave-pulse" size="12px" />
        </div>
      </template>

      <CApexChartLine :height="180" :options="chartOptions" :series="chartData" :width="300" />
    </NTooltip>
    <NTooltip
      v-if="isRecommended"
      class="!bg-base !color-text"
      :style="{
        '--n-color': themeVars.base,
      }"
      :trigger="isTouchScreen ? 'click' : 'hover'"
    >
      <template #trigger>
        <div
          class="flex items-center justify-center w-[24px] h-[24px] rounded-full bg-primary6 color-primary"
          @click.stop
        >
          <CIcon class="relative -left-[1px]" fa-type="far" icon="star" size="12px" />
        </div>
      </template>
      {{ t('canvasUI.CPopularityAndEngagementIcons.recommended') }}
    </NTooltip>
  </div>
</template>
<script setup lang="ts">
import { computed, type ComputedRef } from 'vue'
import { NTooltip } from 'naive-ui'
import { useI18n } from 'vue-i18n'
import type { ApexOptions } from 'apexcharts'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CTag from '@lib/components/CTag/CTag.vue'
import CApexChartLine from '@lib/components/CApexChart/CApexChartLine.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { isTouchScreen } from '@lib/util/userAgents'
import {
  SectionPopularityCategory,
  SectionTrendlineDirection,
  type SectionEngagement,
  type SectionPopularity,
} from '@lib/types/sectionPopularity'
import { dayjs, formatDayMonthBasedOnBrowserLang } from '@lib/util/date'

const props = withDefaults(
  defineProps<{
    isRecommended?: boolean
    popularity: SectionPopularity | undefined
    engagement: SectionEngagement | undefined
    variation?: 'extended' | 'compact' | 'minimal'
    views: number
  }>(),
  {
    isRecommended: false,
    variation: 'extended',
    views: 0,
  },
)

const { t } = useI18n()
const themeVars = useThemeVars()
const hasEngagementData = computed(() => props.engagement?.data.some((value) => typeof value === 'number' && value > 0))
const hasPopularity = computed(() => (props.popularity?.value && props.popularity.value > 0) || props.views > 0)

const chartData: ComputedRef<ApexAxisChartSeries> = computed(() => [
  {
    data: props.engagement?.data ?? [],
    name: t('canvasUI.CPopularityAndEngagementIcons.views'),
  },
])

function generateDates(daysCount: number) {
  const dates: string[] = []
  for (let i = 0; i < daysCount; i++) {
    const date = dayjs()
      .subtract(i + 1, 'day')
      .toDate()
    dates.unshift(formatDayMonthBasedOnBrowserLang(date))
  }
  return dates
}

const chartOptions: ComputedRef<ApexOptions> = computed(() => ({
  chart: {
    animations: {
      speed: 500,
    },
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
  },
  tooltip: {
    x: {
      show: false,
    },
  },
  colors: [
    props.engagement?.direction === SectionTrendlineDirection.ASC ? themeVars.value.success : themeVars.value.error,
  ],
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 2,
  },
  markers: {
    size: 5,
    strokeColors: '#fff',
    strokeWidth: 2,
    hover: {
      size: 7,
    },
  },
  xaxis: {
    categories: generateDates(props.engagement?.data.length ?? 0),
  },
  yaxis: {
    min: 0,
    forceNiceScale: true,
    decimalsInFloat: 0,
    labels: {
      formatter: function (val: number) {
        return val.toFixed(0)
      },
    },
  },
}))

function getPopularityClass(): string {
  switch (props.popularity?.category) {
    case SectionPopularityCategory.NONE:
      return 'color-primary2 bg-primary6'
    case SectionPopularityCategory.POPULAR:
      return 'color-info bg-info2'
    case SectionPopularityCategory.THE_MOST_POPULAR:
      return 'color-base bg-info'
    default:
      return 'color-primary2 bg-primary6'
  }
}

function getEngagementClass(): string {
  if (props.engagement?.direction === SectionTrendlineDirection.ASC) return 'color-success bg-success2'
  else return 'color-primary2 bg-primary6'
}

function getPopularityIcon() {
  switch (props.popularity?.category) {
    case SectionPopularityCategory.NONE:
    case SectionPopularityCategory.POPULAR:
      return 'fire'
    case SectionPopularityCategory.THE_MOST_POPULAR:
      return 'crown'
    default:
      return 'fire'
  }
}
</script>
